import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type {
  GiftCardActionStateMapType,
  GiftCardActionStateType,
  GiftCardActionType,
  GiftCardAppliedCardType,
  GiftCardApplyType,
  GiftCardConfigType,
  GiftCardDeleteStateMapType,
  GiftCardDeleteType,
  GiftCardFlowSteps,
  GiftCardProviderType,
  GiftCardRechargeNoticeType,
  GiftCardRechargeWalletType,
  GiftCardsMapType,
  GiftCardType,
  GiftCardValidateCodeType,
} from "../types";

const IDLE_ACTION_STATE: GiftCardActionStateType = { loading: false, error: null };
const buildActionState = (): GiftCardActionStateMapType => ({
  VALIDATING: { ...IDLE_ACTION_STATE },
  APPLYING: { ...IDLE_ACTION_STATE },
  RECHARGING: { ...IDLE_ACTION_STATE },
});

/** Stand-in for a card's states when there is no active card. Read-only — nothing patches it. */
const INITIAL_ACTION_STATE: GiftCardActionStateMapType = buildActionState();

/** Share of a known balance the amount field starts on, whatever the card's amount strategy is. */
const DEFAULT_REDEEM_SHARE = 0.4;
const defaultRedeemValue = (redeemBalance: number) =>
  Math.round(Math.max(redeemBalance, 0) * DEFAULT_REDEEM_SHARE);

/**
 * Whatever the host rejected with, as an `Error`. A rejection can carry anything — an Axios
 * error, a string, a bare object — and the card's `error` key promises one shape to read.
 */
const toError = (thrown: unknown): Error =>
  thrown instanceof Error
    ? thrown
    : new Error(typeof thrown === "string" ? thrown : "Something went wrong");

/**
 * The error for the first code field the shopper left blank, or null when all are filled. A GYFTR
 * wallet has no code fields — it is looked up by account — so it is never blank.
 */
const getEmptyInputError = (card: GiftCardType): Error | null => {
  if (card.provider === "GYFTR") return null;
  const empty = card.input_config.inputs.find((field) => !card.values[field.title]?.trim());
  return empty ? new Error(`Please enter your ${empty.title}`) : null;
};

/**
 * Seeds each provider config with its initial (empty) popup state.
 * The first available provider (no unavailability reasons) starts active, falling back to the first
 * one so a card is always selected — unless `activeProvider` names one the new config still
 * carries, which keeps the shopper on the card they were already on.
 */
const buildGiftCardsMap = (
  coupons: GiftCardConfigType[],
  activeProvider?: GiftCardProviderType,
): GiftCardsMapType => {
  const keepActive =
    activeProvider && coupons.some((coupon) => coupon.provider === activeProvider)
      ? activeProvider
      : null;
  const initialActive =
    keepActive ??
    (coupons.find((coupon) => !coupon.unavailability_reasons?.length) ?? coupons[0])?.provider;

  return coupons.reduce<GiftCardsMapType>((acc, coupon) => {
    acc[coupon.provider] = {
      ...coupon,
      values: {},
      codeValidated: false,
      redeemBalance: 0,
      is_active: coupon.provider === initialActive,
      redeemValue:0,
      actions: buildActionState()
    };
    return acc;
  }, {});
};

/**
 * Keys to write on a card — either a literal patch or a function of the current
 * card, for updates that build on what is already there (e.g. merging `values`).
 */
export type GiftCardPatchType =
  | Partial<GiftCardType>
  | ((card: GiftCardType) => Partial<GiftCardType>);

export interface GiftContextType {
  giftCards: GiftCardsMapType;
  setGiftCards: Dispatch<SetStateAction<GiftCardsMapType>>;
  /** Cards already attached to the checkout, owned by the host and passed straight through. */
  appliedCards: GiftCardAppliedCardType[];
  /** How many cards this order accepts. Drives the applied list's counter and its add-another row. */
  maxUsage: number;
  /** Screen the popup is currently showing. */
  activeStep: GiftCardFlowSteps;
  setActiveStep: Dispatch<SetStateAction<GiftCardFlowSteps>>;
  activeProvider: GiftCardType | null;
  selectProvider: (provider: GiftCardProviderType) => void;
  /**
   * Rebuilds the provider cards from the host's config, clearing every code, balance and error the
   * last entry left on them — call it before showing the entry screen for another card, otherwise
   * that card opens on the previous one's state.
   *
   * Selection goes back to the first provider unless `keepProvider` is passed, which keeps that one
   * active if the config still carries it.
   */
  resetGiftCards: (keepProvider?: GiftCardProviderType) => void;
  /** Patches any key(s) on one provider's card. No-op if that provider is absent. */
  updateGiftCard: (provider: GiftCardProviderType, patch: GiftCardPatchType) => void;
  /** Same as `updateGiftCard`, targeting whichever card is active. */
  updateActiveGiftCard: (patch: GiftCardPatchType) => void;
  /** Stores one input's value on the active card, keyed by the input title. */
  setInputValue: (title: string, value: string) => void;
  /** Sets how much of the active card's balance to redeem. */
  setRedeemAmount: (amount: number) => void;
  /** Patches one call's loading/error state on one provider's card. */
  setActionState: (
    provider: GiftCardProviderType,
    action: GiftCardActionType,
    state: Partial<GiftCardActionStateType>,
  ) => void;
  /**
   * Loading/error state of the active card's calls, or the initial (idle) state when no card is
   * selected — so a caller can read `actionState.APPLYING.loading` without a null check.
   */
  actionState: GiftCardActionStateMapType;
  /**
   * Looks up the active card's balance and writes it back as `redeemBalance`, tracking the call
   * on the card's `VALIDATING` state. Resolves either way — read the state for the outcome.
   */
  validateActiveCard: () => Promise<void>;
  /**
   * Hands the active card to the host to attach to the checkout, tracking the call on the card's
   * `APPLYING` state. Resolves either way — read the state for the outcome.
   */
  applyActiveCard: () => Promise<void>;
  /**
   * Loads a voucher into the active provider's wallet, tracking the call on the card's
   * `RECHARGING` state, and adds what the load reports to the card's `redeemBalance` — and, on a
   * FULL_AMOUNT provider, to `redeemValue` with it — so the top-up is spendable without re-reading
   * the balance. On CUSTOM the amount is the shopper's, so only the ceiling moves.
   *
   * Resolves `true` only if the load itself succeeded — the caller decides what to do next
   * (typically returning to the entry screen), which is why the outcome is returned and not
   * only left on the state. A no-op if the host passed no `rechargeWallet`.
   */
  rechargeActiveWallet: (voucherCode: string) => Promise<boolean>;
  /** Whether the host wired a top-up call at all — gates the "Add Balance" affordance. */
  canRechargeWallet: boolean;
  /**
   * The last top-up worth announcing, or null once it has been. Set by `rechargeActiveWallet` and
   * read by the entry screen the shopper lands back on, which shows it briefly and then dismisses
   * it — the amount is known here and the screen that announces it is not mounted yet, so the
   * notice waits in the context rather than being handed over directly.
   */
  rechargeNotice: GiftCardRechargeNoticeType | null;
  /**
   * Whether the entry screen should hand the caret to the redeem-amount field: a CUSTOM card whose
   * balance just landed, from a code lookup or a top-up. Both calls clear it on the way in, so it
   * only ever stands for the call that has just finished.
   */
  focusRedeemAmount: boolean;
  /**
   * Whether the active card's balance is worth counting up to. True for a balance the shopper has
   * not watched land yet — a first lookup or a top-up — not for one already shown once. Lives here,
   * above the entry screen, because `GiftCardScreens` unmounts that screen on every step change
   * (e.g. a trip to `WALLET_TOPUP` and back with nothing credited) — a ref on the screen would
   * forget the balance was already shown and replay the count-up for no new amount.
   */
  playIntro: boolean;
  /** Drops the pending notice. The entry screen calls this when the strip's time is up. */
  dismissRechargeNotice: () => void;
  /**
   * Detaches one applied card, tracking the call against that card's `transaction_id` so only the
   * pressed row spins.
   *
   * Resolves `true` if the card came off. A no-op if the host passed no `handleDelete`.
   */
  deleteAppliedCard: (card: GiftCardAppliedCardType) => Promise<boolean>;
  /** That one card's delete state. Idle for a card with no delete in flight or failed. */
  getDeleteState: (transactionId: string) => GiftCardActionStateType;
  /** Whether the host wired a delete call at all — gates each row's remove button. */
  canDeleteCard: boolean;
  /**
   * True when the popup is rendered inside the merchant dashboard (the customise-UI preview) rather
   * than a live checkout. Screens read it to gate dashboard-only affordances — the shopper's
   * checkout must never see them, so anything behind this flag stays off by default.
   */
  dashboardOnly: boolean;
}

export const GiftContext = createContext<GiftContextType | null>(null);

interface GiftProviderProps {
  coupons: GiftCardConfigType[];
  /** Cards the checkout already carries. Empty means the shopper has applied none yet. */
  appliedCards?: GiftCardAppliedCardType[];
  /** How many cards the order accepts. Defaults to however many are applied — i.e. no room left. */
  maxUsage?: number;
  /**
   * The host's calls. They land here rather than being threaded down to the entry screen
   * because the active card they operate on lives in this context.
   */
  validateCode: GiftCardValidateCodeType;
  handleApply: GiftCardApplyType;
  /** Optional: only wallet-shaped providers (today GYFTR) have anything to top up. */
  rechargeWallet?: GiftCardRechargeWalletType;
  /** Optional: omit it and applied cards render without a remove button. */
  handleDelete?: GiftCardDeleteType;
  /**
   * Called instead of switching to a provider the shopper picked, when that provider's own
   * `unavailability_reasons` says it can't be redeemed as-is (e.g. it conflicts with an applied
   * coupon). The host decides what to do with it — typically prompting to replace the coupon.
   */
  onProviderUnavailable?: (card: GiftCardType) => void;
  /**
   * Set it only where the dashboard renders the popup as a preview. Defaults to `false` so the
   * checkout — which never passes it — keeps every dashboard-only feature off.
   */
  dashboardOnly?: boolean;
  children: ReactNode;
}

export const GiftProvider = ({
  coupons,
  appliedCards = [],
  maxUsage,
  validateCode,
  handleApply,
  rechargeWallet,
  handleDelete,
  onProviderUnavailable,
  dashboardOnly = false,
  children,
}: GiftProviderProps) => {
  const [giftCards, setGiftCards] = useState<GiftCardsMapType>(() => buildGiftCardsMap(coupons));
  const [activeStep, setActiveStep] = useState<GiftCardFlowSteps>(
    appliedCards.length > 1 ? "APPLIED_LIST" : "CARD_ENTRY",
  );
  // Applied cards are the host's list, not `giftCards` — so their delete state is held apart from
  // the provider cards' and keyed the way an applied card is identified.
  const [deleteStates, setDeleteStates] = useState<GiftCardDeleteStateMapType>({});
  const [rechargeNotice, setRechargeNotice] = useState<GiftCardRechargeNoticeType | null>(null);
  const [focusRedeemAmount, setFocusRedeemAmount] = useState(false);

  const activeProvider = useMemo(
    () => Object.values(giftCards).find((card) => card?.is_active) ?? null,
    [giftCards],
  );

  const updateGiftCard = useCallback(
    (provider: GiftCardProviderType, patch: GiftCardPatchType) => {
      setGiftCards((prev) => {
        const card = prev[provider];
        if (!card) return prev;

        return {
          ...prev,
          [provider]: {
            ...card,
            ...(typeof patch === "function" ? patch(card) : patch),
          },
        };
      });
    },
    [],
  );

  const updateActiveGiftCard = useCallback(
    (patch: GiftCardPatchType) => {
      setGiftCards((prev) => {
        const entry = Object.entries(prev).find(([, card]) => card?.is_active);
        if (!entry) return prev;

        const [provider, card] = entry as [GiftCardProviderType, GiftCardType];
        return {
          ...prev,
          [provider]: {
            ...card,
            ...(typeof patch === "function" ? patch(card) : patch),
          },
        };
      });
    },
    [],
  );

  const setActionState = useCallback(
    (
      provider: GiftCardProviderType,
      action: GiftCardActionType,
      state: Partial<GiftCardActionStateType>,
    ) => {
      updateGiftCard(provider, (card) => ({
        actions: {
          ...card.actions,
          [action]: { ...card.actions[action], ...state },
        },
      }));
    },
    [updateGiftCard],
  );

  const actionState = activeProvider?.actions ?? INITIAL_ACTION_STATE;

  // Balance last observed per provider — the only way to tell a render carrying a new balance
  // (a lookup landing, a top-up) from one merely repeating a balance already seen.
  const [lastBalances, setLastBalances] = useState<Partial<Record<GiftCardProviderType, number>>>(
    {},
  );
  const [introDue, setIntroDue] = useState<Partial<Record<GiftCardProviderType, boolean>>>({});

  const activeBalance = activeProvider?.redeemBalance ?? 0;

  if (activeProvider && lastBalances[activeProvider.provider] !== activeBalance) {
    const provider = activeProvider.provider;
    setLastBalances((prev) => ({ ...prev, [provider]: activeBalance }));
    setIntroDue((prev) => ({ ...prev, [provider]: activeBalance > 0 }));
  }

  const isBalanceSettled = activeBalance > 0 && !actionState.VALIDATING.loading;
  const playIntro = isBalanceSettled && !!activeProvider && !!introDue[activeProvider.provider];

  useEffect(() => {
    if (!playIntro || !activeProvider) return;
    const provider = activeProvider.provider;
    setIntroDue((prev) => (prev[provider] ? { ...prev, [provider]: false } : prev));
  }, [playIntro, activeProvider]);

  const setInputValue = useCallback(
    (title: string, value: string) => {
      // Editing an input invalidates the balance fetched for the previous code, and the failure
      // it earned — neither was ever about what is typed now.
      updateActiveGiftCard((card) => ({
        values: { ...card.values, [title]: value },
        codeValidated: false,
        redeemBalance: 0,
        redeemValue: 0,
        actions: {
          ...card.actions,
          VALIDATING: { ...card.actions.VALIDATING, error: null },
          APPLYING: { ...card.actions.APPLYING, error: null },
        },
      }));
    },
    [updateActiveGiftCard],
  );

  const setRedeemAmount = useCallback(
    (amount: number) => {
      updateActiveGiftCard((card) => ({
        redeemValue: Math.min(Math.max(amount, 0), card.redeemBalance),
      }));
    },
    [updateActiveGiftCard],
  );

  const validateCard = useCallback(
    async (card: GiftCardType | null | undefined) => {
      if (!card || card.actions.VALIDATING.loading) return;

      // Back to the default before the call that may raise it again: a lookup that fails, or one
      // whose balance the shopper has already typed past, must not leave a stale request standing.
      setFocusRedeemAmount(false);

      const { provider, values } = card;

      const emptyInputError = getEmptyInputError(card);
      if (emptyInputError) {
        setActionState(provider, "VALIDATING", { loading: false, error: emptyInputError });
        return;
      }

      const [cardNumberInput, pinInput] = card.input_config?.inputs ?? [];
      const cardNumber = cardNumberInput ? values[cardNumberInput.title] ?? "" : "";
      const pin = pinInput ? values[pinInput.title] : undefined;

      setActionState(provider, "VALIDATING", { loading: true, error: null });

      try {
        const redeemBalance = await validateCode(provider, cardNumber, pin);

        updateGiftCard(provider, (current) => {
          // `setInputValue` replaces `values` wholesale, so a different object here means the
          // shopper kept typing while the call was in flight. The balance belongs to a code that
          // is no longer in the inputs, and showing it against the new one would be a lie.
          if (current.values !== values) {
            return {
              actions: { ...current.actions, VALIDATING: { loading: false, error: null } },
            };
          }

          // A freshly validated code has no amount the shopper has committed to yet: a full-amount
          // card spends its whole balance, and one left to divide opens on the default share
          // rather than on whatever the field happened to hold before the balance was known.
          return {
            codeValidated: true,
            redeemBalance,
            redeemValue:
              current.amount_strategy === "FULL_AMOUNT"
                ? redeemBalance
                : defaultRedeemValue(redeemBalance),
            actions: { ...current.actions, VALIDATING: { loading: false, error: null } },
          };
        });

        // A balance the shopper has to divide is the one case with something left to type.
        if (card.amount_strategy === "CUSTOM") setFocusRedeemAmount(true);
      } catch (error) {
        setActionState(provider, "VALIDATING", { loading: false, error: toError(error) });
      }
    },
    [setActionState, updateGiftCard, validateCode],
  );

  const validateActiveCard = useCallback(
    () => validateCard(activeProvider),
    [activeProvider, validateCard],
  );

  const validateWalletCard = useCallback(
    (card: GiftCardType | null | undefined) => {
      if(dashboardOnly) {
        void validateCard(card)
        return
      }
      if (
        !card ||
        card.provider !== "GYFTR" ||
        card.codeValidated ||
        card.actions.VALIDATING.loading
      )
        return;

      void validateCard(card);
    },
    [validateCard,dashboardOnly],
  );

  const selectProvider = useCallback(
    (provider: GiftCardProviderType) => {
      const card = giftCards[provider];
      if (card?.unavailability_reasons?.length && onProviderUnavailable) {
        onProviderUnavailable(card);
        return;
      }

      setGiftCards((prev) =>
        Object.entries(prev).reduce<GiftCardsMapType>((acc, [key, card]) => {
          acc[key as GiftCardProviderType] = { ...card, is_active: key === provider };
          return acc;
        }, {}),
      );

      validateWalletCard(giftCards[provider]);
    },
    [giftCards, validateWalletCard, onProviderUnavailable],
  );


  const resetGiftCards = useCallback(
    (keepProvider?: GiftCardProviderType) => {
      const nextCards = buildGiftCardsMap(coupons, keepProvider);
      setGiftCards(nextCards);

      const nextActiveCard = Object.values(nextCards).find((card) => card?.is_active);
      if (nextActiveCard?.unavailability_reasons?.length && onProviderUnavailable) {
        onProviderUnavailable(nextActiveCard);
        return;
      }

      validateWalletCard(nextActiveCard);
    },
    [coupons, validateWalletCard, onProviderUnavailable],
  );

  useEffect(() => {
    resetGiftCards(activeProvider?.provider);
  }, [coupons]);

  const applyActiveCard = useCallback(async () => {
    if (!activeProvider || activeProvider.actions.APPLYING.loading) return;

    const { provider } = activeProvider;

    const emptyInputError = getEmptyInputError(activeProvider);
    if (emptyInputError) {
      setActionState(provider, "APPLYING", { loading: false, error: emptyInputError });
      return;
    }

    setActionState(provider, "APPLYING", { loading: true, error: null });

    try {
      await handleApply(activeProvider);
      setActionState(provider, "APPLYING", { loading: false, error: null });
      if (Number(maxUsage ?? 0) > 1) setActiveStep("APPLIED_LIST");
    } catch (error) {
      setActionState(provider, "APPLYING", { loading: false, error: toError(error) });
    }
  }, [activeProvider, handleApply, maxUsage, setActionState]);

  const rechargeActiveWallet = useCallback(
    async (voucherCode: string) => {
      if (!rechargeWallet || !activeProvider) return false;
      if (activeProvider.actions.RECHARGING.loading) return false;

      // Same reset as `validateCard`: the request this call may raise starts from the default.
      setFocusRedeemAmount(false);

      const { provider } = activeProvider;
      setActionState(provider, "RECHARGING", { loading: true, error: null });

      let loaded = 0;
      try {
        loaded = await rechargeWallet(provider, voucherCode);
      } catch (error) {
        setActionState(provider, "RECHARGING", { loading: false, error: toError(error) });
        return false;
      }

      // The load's own answer is the balance update — there is no balance re-read behind it. What
      // the voucher added goes on top of what the wallet already held, so the top-up is spendable
      // without validating the wallet again.
      const credited = Number.isFinite(loaded) ? Math.max(loaded, 0) : 0;

      updateGiftCard(provider, (card) => {
        const redeemBalance = card.redeemBalance + credited;

        // A wallet holding something is a wallet with a known balance — which is what the entry
        // screen gates its slider and its apply on. Nothing loaded leaves it as it was. A top-up
        // adds to what the shopper can spend; it is not a decision about what they will — whatever
        // they had already dialled in survives the load, and only a wallet they had picked no
        // amount on yet opens on the default share of the balance the load just created.
        return {
          codeValidated: redeemBalance > 0,
          redeemBalance,
          redeemValue:
            card.amount_strategy === "FULL_AMOUNT"
              ? redeemBalance
              : card.redeemValue > 0
                ? card.redeemValue
                : defaultRedeemValue(redeemBalance),
          actions: { ...card.actions, RECHARGING: { loading: false, error: null } },
        };
      });

      // A voucher that credited nothing is not news — only a load that moved the balance gets
      // announced, and only until the entry screen has shown it.
      if (credited > 0) setRechargeNotice({ provider, amount: credited });

      // The shopper lands back on the entry screen with the topped-up balance still to divide.
      setFocusRedeemAmount(true);

      return true;
    },
    [activeProvider, rechargeWallet, setActionState, updateGiftCard],
  );

  const dismissRechargeNotice = useCallback(() => setRechargeNotice(null), []);

  const getDeleteState = useCallback(
    (transactionId: string) => deleteStates[transactionId] ?? IDLE_ACTION_STATE,
    [deleteStates],
  );

  const patchDeleteState = useCallback(
    (transactionId: string, state: Partial<GiftCardActionStateType>) => {
      setDeleteStates((prev) => ({
        ...prev,
        [transactionId]: { ...(prev[transactionId] ?? IDLE_ACTION_STATE), ...state },
      }));
    },
    [],
  );

  const deleteAppliedCard = useCallback(
    async (card: GiftCardAppliedCardType) => {
      if (!handleDelete) return false;

      const transactionId = card.transaction_id;
      if (deleteStates[transactionId]?.loading) return false;

      patchDeleteState(transactionId, { loading: true, error: null });

      try {
        await handleDelete(card);
      } catch (error) {
        patchDeleteState(transactionId, { loading: false, error: toError(error) });
        return false;
      }

      // The card is off the host's list now, so drop its entry rather than keep state against a
      // transaction that no longer exists — and so a re-applied card starts idle, not resolved.
      setDeleteStates((prev) => {
        if (!(transactionId in prev)) return prev;
        const next = { ...prev };
        delete next[transactionId];
        return next;
      });
      return true;
    },
    [deleteStates, handleDelete, patchDeleteState],
  );

  const value = useMemo<GiftContextType>(
    () => ({
      giftCards,
      setGiftCards,
      appliedCards,
      maxUsage: maxUsage ?? appliedCards.length,
      activeStep,
      setActiveStep,
      selectProvider,
      activeProvider,
      resetGiftCards,
      updateGiftCard,
      updateActiveGiftCard,
      setInputValue,
      setRedeemAmount,
      setActionState,
      actionState,
      validateActiveCard,
      applyActiveCard,
      rechargeActiveWallet,
      canRechargeWallet: Boolean(rechargeWallet),
      rechargeNotice,
      focusRedeemAmount,
      playIntro,
      dismissRechargeNotice,
      deleteAppliedCard,
      getDeleteState,
      canDeleteCard: Boolean(handleDelete),
      dashboardOnly,
    }),
    [
      giftCards,
      appliedCards,
      maxUsage,
      activeStep,
      selectProvider,
      activeProvider,
      resetGiftCards,
      updateGiftCard,
      updateActiveGiftCard,
      setInputValue,
      setRedeemAmount,
      setActionState,
      actionState,
      validateActiveCard,
      applyActiveCard,
      rechargeActiveWallet,
      rechargeWallet,
      rechargeNotice,
      focusRedeemAmount,
      playIntro,
      dismissRechargeNotice,
      deleteAppliedCard,
      getDeleteState,
      handleDelete,
      dashboardOnly,
    ],
  );

  return <GiftContext.Provider value={value}>{children}</GiftContext.Provider>;
};

export const useGiftCardContext = () => {
  const ctx = useContext(GiftContext);
  if (!ctx) {
    throw new Error("useGiftContext must be used within a GiftProvider");
  }
  return ctx;
};
